// /src/components/tools/HoldToConfirmButton.jsx

import React, { useEffect, useRef, useState } from 'react';

export default function HoldToConfirmButton({
  children,
  holdMs = 5000,
  onConfirm,
  disabled = false,
  className = '',
  holdingLabel = null,
  completedLabel = null,
  /** When > 0: show completedLabel only after onConfirm succeeds, then hide after this many ms. */
  completedDisplayMs = 0,
  style,
  ...buttonProps
}) {
  const timerRef = useRef(null);
  const completedTimerRef = useRef(null);
  const [holding, setHolding] = useState(false);
  const [pending, setPending] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
    };
  }, []);

  const clearHold = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setHolding(false);
  };

  const startHold = () => {
    if (disabled || holding || pending || completed) return;

    setHolding(true);

    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      setHolding(false);

      if (completedDisplayMs > 0) {
        setPending(true);
        try {
          if (typeof onConfirm === 'function') {
            await onConfirm();
          }
          setPending(false);
          setCompleted(true);
          completedTimerRef.current = setTimeout(() => {
            completedTimerRef.current = null;
            setCompleted(false);
          }, completedDisplayMs);
        } catch {
          setPending(false);
        }
        return;
      }

      setCompleted(true);
      try {
        if (typeof onConfirm === 'function') {
          await onConfirm();
        }
      } finally {
        setCompleted(false);
      }
    }, holdMs);
  };

  return (
    <button
      {...buttonProps}
      type={buttonProps.type || 'button'}
      className={[
        'hold-confirm-btn',
        holding ? 'is-holding' : '',
        completed ? 'is-completed' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--hold-confirm-ms': `${holdMs}ms`, ...style }}
      disabled={disabled || pending || completed}
      onMouseDown={startHold}
      onMouseUp={clearHold}
      onMouseLeave={clearHold}
      onTouchStart={startHold}
      onTouchEnd={clearHold}
      onTouchCancel={clearHold}
      onKeyDown={(e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        if (e.repeat) return;
        startHold();
      }}
      onKeyUp={(e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        clearHold();
      }}
    >
      <span className="hold-confirm-btn__progress" />
      <span className="hold-confirm-btn__label">
        {completed
          ? completedLabel || children
          : holding
          ? holdingLabel || children
          : children}
      </span>
    </button>
  );
}